// NOTICE: This file was copied from the oppizi-shared repository, do
// not modify it here.

// NOTICE: This file was copied from the oppizi-shared repository, do
// not modify it here.

import _ from 'lodash'
import moment from 'moment'

import {
  getDistanceToPolygonBorder,
  getDistance,
  isPointInPolygon,
} from './libraries/geo'

import {
  STATUS_DRAFT,
  STATUS_READY_TO_PUBLISH,
  MISSION_DISPLAY_STATUS_READY_TO_PUBLISH,
  STATUS_AVAILABLE,
  STATUS_BOOKED,
  STATUS_CANCELLED,
  STATUS_CHECKED_IN,
  STATUS_UNDER_REVIEW,
  STATUS_COMPLETED,
  STATUS_PAID_MISSION,
  MISSION_STATUS_ENUM,
  TOO_FAR_FROM_LOCATION_ERROR,
  MISSION_NOT_YET_ACTIVE_ERROR,
  MISSION_EXPIRED_ERROR,
  MISSION_ENUM_TYPES,
  MISSION_LB_NOT_YET_ACTIVE_ERROR,
  MISSION_LB_EXPIRED_ERROR,
  TOO_FAR_FROM_BA_LOCATION_ERROR,
  MISSION_TYPES,
  MISSION_CHECK_IN_EXPIRED_ERROR,
} from './constants'
import { InvalidRequest } from './error'
import { calculateMedianWeightExcludingOutliers } from './stats'
import {
  isLocalDateStr,
  getWeekNumberAndYearInTz,
  addMinutesToDate,
  dropTimeZoneFromDate,
  changeTimezone,
} from './libraries/utils/date'

const HTH_CHECK_IN_RADIUS = 100
const LB_CHECK_IN_RADIUS = 50

export const checkInValidation = {
  validatePosition: (mission, data, checkinRadius) => {
    return mission.type === MISSION_ENUM_TYPES.LETTERBOX
      ? checkInValidation.validatePositionLB(mission, data)
      : checkInValidation.validatePositionHTH(mission, data, checkinRadius)
  },
  validatePositionHTH: (mission, data, checkinRadius = HTH_CHECK_IN_RADIUS) => {
    const { latitude, longitude } = mission.location
    const { latitude: currentLat, longitude: currentLng } = data
    const checkinDistance = _.round(
      getDistance(latitude, longitude, currentLat, currentLng),
      0,
    )
    if (checkinDistance > checkinRadius) {
      var err = new InvalidRequest(
        `You are too far from the mission location (${checkinDistance} meters)`,
      )
      err['distance'] = checkinDistance
      err['code'] = TOO_FAR_FROM_LOCATION_ERROR
      throw err
    }
  },
  validatePositionLB: (mission, userLocation) => {
    const point = [userLocation.longitude, userLocation.latitude]
    const polygon = mission.boundary.map(p => [p.longitude, p.latitude])

    if (
      !isPointInPolygon(point, polygon) &&
      getDistanceToPolygonBorder(point, polygon) > LB_CHECK_IN_RADIUS
    ) {
      var err = new InvalidRequest(`You are not in the mission area`)
      err['distance'] = Math.floor(getDistanceToPolygonBorder(point, polygon))
      err['code'] = TOO_FAR_FROM_LOCATION_ERROR
      throw err
    }
  },
  validatePositionSpyToBa: (
    baLocation,
    spyLocation,
    checkinRadius = HTH_CHECK_IN_RADIUS,
  ) => {
    const { latitude, longitude } = baLocation
    const { latitude: currentLat, longitude: currentLng } = spyLocation
    const checkinDistance = _.round(
      getDistance(latitude, longitude, currentLat, currentLng),
      0,
    )
    if (checkinDistance > checkinRadius) {
      var err = new InvalidRequest(
        `You are too far from the brand ambassador's location (${checkinDistance} meters)`,
      )
      err['distance'] = checkinDistance
      err['code'] = TOO_FAR_FROM_BA_LOCATION_ERROR
      throw err
    }
  },
  // TODO: deprecate surge interval
  validateTime: ({
    mission,
    additionalMinutes = 0,
    cityCheckinMinutesBefore,
    cityCheckinMinutesAfter,
    surgeInterval,
    now,
    timezone,
  }) => {
    return mission.type === MISSION_ENUM_TYPES.LETTERBOX ||
      mission.type === MISSION_TYPES.LETTERBOX
      ? checkInValidation.validateTimeLB({
          missionDateTime: mission.timeRangeStart,
          now,
          timezone,
          cityCheckinMinutesBefore,
          cityCheckinMinutesAfter,
        })
      : checkInValidation.validateTimeHTH({
          missionDateTime: mission.timeRangeStart,
          additionalMinutes,
          cityCheckinMinutesBefore,
          cityCheckinMinutesAfter,
          surgeInterval,
          now,
        })
  },
  validateTimeHTH: ({
    missionDateTime,
    additionalMinutes = 0,
    cityCheckinMinutesBefore,
    cityCheckinMinutesAfter,
    bonusCheckInTimeBefore,
    bonusCheckInTimeAfter,
    now,
  }) => {
    now = moment(now)
    const timeRangeStart = moment(missionDateTime)
    const minutesTimediff = _.round((now - timeRangeStart) / 60000, 0)

    // The max is figured out this way to filter out any undefined values that would
    // return NaN, and this makes the largest value take precedence
    const minutesBefore = Math.max.apply(
      null,
      [cityCheckinMinutesBefore, bonusCheckInTimeBefore].filter(
        val => val || null,
      ),
    )
    const minutesAfter = Math.max.apply(
      null,
      [cityCheckinMinutesAfter, bonusCheckInTimeAfter].filter(
        val => val || null,
      ),
    )

    if (minutesTimediff > minutesAfter + additionalMinutes) {
      // mission is supposed to be started xx minutes ago, it's too late
      var minutesElapsed = minutesTimediff - minutesAfter
      var err = new InvalidRequest(
        `This mission is not active anymore, its checkin time has expired (${minutesElapsed} minutes ago)`,
      )
      err['code'] = MISSION_EXPIRED_ERROR
      err['minutesElapsed'] = minutesElapsed
      throw err
    }

    if (minutesTimediff < -minutesBefore - additionalMinutes) {
      // mission is supposed to start in xx minutes, it's too early
      var minutesUntilActive = Math.abs(minutesTimediff + minutesBefore)
      var err = new InvalidRequest(
        `This mission is not active yet, it will be active in ${minutesUntilActive} minutes`,
      )
      err['code'] = MISSION_NOT_YET_ACTIVE_ERROR
      err['minutesUntilActive'] = minutesUntilActive
      throw err
    }
  },
  validateTimeForSpy: ({
    mission,
    cityCheckinMinutesAfter,
    cityCheckinMinutesBefore,
    now = new Date(),
    shiftDuration,
  }) => {
    now = moment(now)
    const missionDateTime = mission.timeRangeStart
    const differenceInMinutes = (now - new Date(missionDateTime)) / 1000 / 60
    const differenceInHours = (now - new Date(missionDateTime)) / 1000 / 60 / 60

    if (differenceInHours > shiftDuration) {
      const err = new InvalidRequest(`This mission is not active anymore`)
      err['code'] = MISSION_EXPIRED_ERROR
      throw err
    }

    if (differenceInMinutes < -cityCheckinMinutesBefore) {
      const value = Math.floor(
        Math.abs(+differenceInMinutes) - cityCheckinMinutesBefore,
      )
      const err = new InvalidRequest(
        `This mission is not active yet, it will be active in ${value} minutes`,
      )
      err['code'] = MISSION_LB_NOT_YET_ACTIVE_ERROR
      err['minutesDifference'] = value
      throw err
    } else if (
      Boolean(cityCheckinMinutesAfter) &&
      differenceInMinutes >= cityCheckinMinutesAfter
    ) {
      const value = Math.floor(Math.abs(+differenceInMinutes))
      const err = new InvalidRequest(
        `This mission available time to checkin has ended ${value} minutes ago`,
      )
      err['code'] = MISSION_CHECK_IN_EXPIRED_ERROR
      err['minutesDifference'] = value
      throw err
    }
  },
  validateTimeHTHForOps: ({
    newMissionDateTime,
    oldMissionDateTime,
    timezone,
    cityCheckinMinutesBefore,
  }) => {
    const missionDateTz = dropTimeZoneFromDate(
      changeTimezone(
        newMissionDateTime
          ? new Date(newMissionDateTime).toISOString()
          : new Date(oldMissionDateTime).toISOString(),
        timezone,
      ),
    )
    const nowTz = dropTimeZoneFromDate(
      changeTimezone(new Date().toISOString(), timezone),
    )
    if (
      new Date(missionDateTz) >
      addMinutesToDate(new Date(nowTz), cityCheckinMinutesBefore)
    ) {
      throw new Error(
        'Can`t check-in to the mission where start date in a future.',
      )
    }
  },
  validateTimeLB: ({
    missionDateTime,
    now = new Date(),
    cityCheckinMinutesBefore,
    cityCheckinMinutesAfter,
  }) => {
    now = moment(now)
    const differenceInMinutes = (now - new Date(missionDateTime)) / 1000 / 60

    if (differenceInMinutes > cityCheckinMinutesAfter) {
      const err = new InvalidRequest(`This mission is not active anymore`)
      err['code'] = MISSION_LB_EXPIRED_ERROR
      throw err
    }

    if (differenceInMinutes < -cityCheckinMinutesBefore) {
      const value = Math.floor(
        Math.abs(+differenceInMinutes) - cityCheckinMinutesBefore,
      )
      const err = new InvalidRequest(
        `This mission is not active yet, it will be active in ${value} minutes`,
      )
      err['code'] = MISSION_LB_NOT_YET_ACTIVE_ERROR
      err['minutesDifference'] = value
      throw err
    }
  },
  validateTimeLBForOps: ({ missionDateTime, timezone }) => {
    const missionWeekData = getWeekNumberAndYearInTz(
      new Date(missionDateTime),
      timezone,
    )
    const currentWeekData = getWeekNumberAndYearInTz(new Date(), timezone)

    if (
      currentWeekData.weekNumber < missionWeekData.weekNumber &&
      currentWeekData.year <= missionWeekData.year
    ) {
      throw new Error(
        'Can`t check-in to the mission where start date in a future weeks.',
      )
    }
  },
}

/**
 * Specifies the various different mission states
 */
export const MISSION_STATE = {
  // Is 100 meters outside of the mission location
  OUTSIDE_OF_MISSION_AREA: 1,
  // There has been no activity update in over 20 minutes
  OFFLINE: 1 << 1,
}

/**
 * @typedef {MissionActivity}
 * @property {number} longitude
 * @property {numner} latitude
 * @property {Date} timestamp
 *
 * @param {Date} now The current date
 * @param {Array<MissionActivity>} activity
 * @returns {Number} The bit value describing the mission state. See
 * `MISSION_STATE`. So if the BA is outside of the mission area and offline
 * then the value returned would be 3.
 */
export const getMissionStateFromActivity = function(now, activity, mission) {
  // Sort in reverse order, newest data to oldest
  activity = activity.slice(0)
  activity.sort(function(a, b) {
    if (a.timestamp < b.timestamp) return 1
    if (a.timestamp > b.timestamp) return -1
    return 0
  })

  var state = 0

  if (mission.dateTime === undefined)
    throw new InvalidRequest("Mission is missing the 'dateTime' prop.")

  // Work out if offline
  var offlineMinutesAgo = new Date(now)
  offlineMinutesAgo.setMinutes(offlineMinutesAgo.getMinutes() - 20)
  if (
    offlineMinutesAgo >= mission.dateTime &&
    (activity.length == 0 || activity[0].timestamp < offlineMinutesAgo)
  ) {
    state = state | MISSION_STATE.OFFLINE
  }
  if (mission.latitude === undefined)
    throw new InvalidRequest("Mission is missing the 'latitude' prop.")
  if (mission.longitude === undefined)
    throw new InvalidRequest("Mission is missing the 'longitude' prop.")

  // Work out if outside the mission area
  // Only outside if all three of the last positions are outside
  var isOutside = true
  for (var i = 0; i < Math.min(activity.length, 3); i++) {
    var thisActivity = activity[i]
    var distance = getDistance(
      mission.latitude,
      mission.longitude,
      thisActivity.latitude,
      thisActivity.longitude,
    )

    if (distance <= 100) {
      isOutside = false
    }
  }
  if (isOutside) {
    state = state | MISSION_STATE.OUTSIDE_OF_MISSION_AREA
  }

  return state
}

/**
 * Convert the status enum to its numeric equivalent as stored in the database
 * @param {MISSION_STATUS_ENUM} status
 * @return {number}
 */
export const convertStatusEnumToNumericValue = function(status) {
  switch (status) {
    case MISSION_STATUS_ENUM.DRAFT:
      return STATUS_DRAFT
    // Display-only state; ordered as the draft it still is.
    case MISSION_DISPLAY_STATUS_READY_TO_PUBLISH:
      return STATUS_READY_TO_PUBLISH
    case MISSION_STATUS_ENUM.AVAILABLE:
      return STATUS_AVAILABLE
    case MISSION_STATUS_ENUM.BOOKED:
      return STATUS_BOOKED
    case MISSION_STATUS_ENUM.CANCELLED:
      return STATUS_CANCELLED
    case MISSION_STATUS_ENUM.CHECKED_IN:
      return STATUS_CHECKED_IN
    case MISSION_STATUS_ENUM.UNDER_REVIEW:
      return STATUS_UNDER_REVIEW
    case MISSION_STATUS_ENUM.COMPLETED:
      return STATUS_COMPLETED
    case MISSION_STATUS_ENUM.PAID:
      return STATUS_PAID_MISSION
    default:
      throw new Error(`Unrecognised status ${status}.`)
  }
}

/**
 * Ranks a mission compared to its peers.
 * @param {number} totalConversions How many conversions the mission has
 * @param {Array<number} peersTotalConversions Comparative conversion totals
 * for other missions at the same elapsed time since inception.
 * @returns {number} A rank value between 0 and 1
 */
export const rankMission = (totalConversions, peersTotalConversions) => {
  // We treat 0 the same as no data, but we do want zeros to affect actual
  // results so we don't replace the peersTotalConversions here
  if (peersTotalConversions.filter(v => v).length < 3) return null
  // We don't rank unless we have sufficient comparison data
  if (peersTotalConversions.length < 3) return null
  var range = Array.from(peersTotalConversions)
  range.push(totalConversions)
  range.sort(function(a, b) {
    if (Number(a) < Number(b)) return -1
    if (Number(a) > Number(b)) return 1
    return 0
  })
  return calculateMedianWeightExcludingOutliers(totalConversions, range)
}

// We will rank locations across the day with a granularity of every 30 mins
export const TIME_OF_DAY_GRANULARITY_MS = 1000 * 60 * 30
// We treat every week as a new range, so our time of day range that we treat
// each day differently is weekly
export const TIME_OF_DAY_RANGE_MS = 1000 * 60 * 60 * 24 * 7

/**
 * Split the week into 30-mins slots,
 * in such way that same time for same day of week will result with same time slot.
 *
 * So Monday through to Sunday 30 minute slots
 *
 * Example: Each Monday from 00:00:00 to 00:29:59 will result 0
 * Example: Each Monday from 00:30:00 to 00:59:59 will result 30
 * Example: Each Sunday from 12:00:00 to 12:29:59 will result 9360
 * @param {string} localDateTime
 * @return {number} Time slot from 0 to 10050
 */
export const getLocalTimeSlotFromDateTime = localDateTime => {
  if (!localDateTime || !isLocalDateStr(localDateTime))
    throw new Error('Expected a local date.')
  localDateTime = new Date(localDateTime)
  var mins = Math.floor(localDateTime.getMinutes() / 30) * 30
  var hours = localDateTime.getHours()
  var day = localDateTime.getDay()
  // Adjust from Sun to Say to Mon to Sun
  day = (day + 6) % 7
  return day * (60 * 24) + hours * 60 + mins
}
